/**
 * Did a failed `POST /auth/refresh` actually mean the session is over?
 *
 * The refresh paths used to treat EVERY rejection as a dead session and wipe
 * the access token + cached user. That is wrong for a transient failure, and
 * it had a real user-visible cost: a request loop used up the general per-IP
 * rate-limit budget, so `/auth/refresh` answered **429**, which signed a host
 * out mid-broadcast-studio and then showed them "Your session has expired for
 * security reasons" (`INVALID_TOKEN` in utils/errorMessages) over a session
 * that was perfectly valid. Being briefly offline, a 502 during a deploy, and
 * a request timeout all did the same thing.
 *
 * The endpoint answers 400 when no refresh cookie was sent and 401 when the
 * cookie is invalid, revoked or expired (routes/auth.js `POST /refresh`).
 * Those — plus 403 for symmetry — are the only answers that mean "this
 * session is over". Everything else, and **no response at all**, leaves the
 * session alone: it says nothing about whether the refresh cookie is still
 * good, the access token in hand may still work, and the next request retries
 * the refresh anyway, so a transient failure self-heals.
 *
 * Fails toward KEEPING the session, deliberately. Being signed out wrongly
 * loses a host their place in a live show; keeping a session a moment too long
 * costs nothing, because every request is still authorised server-side.
 */
const SESSION_ENDED_STATUSES = [400, 401, 403];

export function refreshMeansSessionEnded(error) {
  const status = error?.response?.status;
  return SESSION_ENDED_STATUSES.includes(status);
}

export default refreshMeansSessionEnded;
